export interface RuntimeConfig {
  apiBase: string;
  facebookUrl: string;
}

declare global {
  interface Window {
    FACEGO_ACCOUNT_CONFIG?: Partial<RuntimeConfig>;
    turnstile?: {
      render: (element: Element, options: Record<string, unknown>) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    facegoTurnstileReady?: () => void;
  }
}

const runtime = window.FACEGO_ACCOUNT_CONFIG || {};

export const apiBase = String(runtime.apiBase || "http://localhost:8000/api/v1").replace(/\/+$/, "");
export const facebookUrl = runtime.facebookUrl || "https://facebook.com";

export const TOKEN_KEY = "facego_token";
export const CART_KEY = "facego_member_cart";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

export function readToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || "";
  } catch {
    return "";
  }
}

export function writeToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Không lưu được thì phiên chỉ sống trong tab hiện tại.
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Không có gì để dọn.
  }
}

interface ApiOptions {
  method?: string;
  body?: unknown;
  authenticated?: boolean;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = "GET", body, authenticated = false, signal } = options;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (authenticated) headers.Authorization = `Bearer ${readToken()}`;

  const response = await fetch(apiBase + path, {
    method,
    headers,
    signal,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  const payload = (await response.json().catch(() => ({}))) as {
    message?: string;
    code?: string;
  };

  if (!response.ok) {
    // Token hết hạn thì dọn ngay, nếu không mọi request sau đều thất bại như cũ.
    if (authenticated && response.status === 401) clearToken();
    throw new ApiError(
      payload.message || "Không thể hoàn tất yêu cầu. Thử lại sau.",
      payload.code || "request_failed",
      response.status,
    );
  }

  return payload as T;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return "Không thể hoàn tất yêu cầu. Thử lại sau.";
}
